import * as PIXI from 'pixi.js';
import { assertNumberBetween } from '../../validators';
import type { UpdateFlowMixinSettings } from '../updateFlowMixin';
import { TypedUpdateFlowMixin } from '../updateFlowMixin';
import type { SerializableMixinSettings } from '../serializableMixin';
import { TypedSerializableMixin } from '../serializableMixin';
import type { BaseAsset } from '../asset/BaseAsset';

export type StencilMaskSettings = {
    updateTarget: BaseAsset;
    parent?: never; // parent should not be used
} & SerializableMixinSettings &
    UpdateFlowMixinSettings;

/**
 * The StencilMask is a base class for our masks that can be applied to a BaseAsset.
 *
 * Stencil masking discards pixels that don't overlap with the pixels rendered into the stencil buffer.
 * It is not as fast as a Scissor Mask, but nonetheless still quite performant.
 *
 * Note that this class does not use the Parent mixin, because setting the parent property on a PIXI.Graphics
 * makes it get positioned to relative to the parent. However, our masks were originally built to be
 * globally positioned, so it's simpler to just not use the parent property. This does mean though that
 * this class doesn't exactly follow our pattern of using the mixins, but it's pretty close. The notable
 * difference is that it implements its own updateTarget to notify a change instead of using its parent.
 */
export abstract class StencilMask extends TypedUpdateFlowMixin(
    TypedSerializableMixin(PIXI.Graphics),
) {
    #transition: number = 0;
    protected updateTarget: BaseAsset = null;

    constructor(settings: StencilMaskSettings) {
        super(settings);
        this.updateTarget = settings.updateTarget;
    }

    abstract getMaxBounds(): PIXI.Rectangle;

    get root() {
        return this.updateTarget.root;
    }

    override _emitChanged() {
        this.updateTarget.requiresRedraw = true;
    }

    /**
     * The point along the shape's progression from its start state to end state.
     */
    get transition() {
        return this.#transition;
    }

    set transition(value: number) {
        if (this.transition === value) {
            return;
        }
        assertNumberBetween(value, 0, 1);
        this.#transition = value;
        this.requiresUpdate = true;
    }

    override get propertiesToSerialize(): string[] {
        return [];
    }

    override get readonlyProperties() {
        return ['classType'];
    }

    createHelpers(): PIXI.DisplayObject[] {
        return [];
    }
}
